import Image from 'next/image'
import clsx from 'clsx'
import { useForm } from 'react-hook-form'
import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Textarea } from '@wartek-id/textarea'
import { Button } from '@wartek-id/button-v1'
import { Radio, RadioGroup } from '@wartek-id/radio'
import { Spinner } from '@wartek-id/spinner'
import { useToast } from '@wartek-id/toast'
import type { DialogProps } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Checkbox } from '@wartek-id/checkbox-v1'

import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'app/KinerjaPSPegawaiPage/components/Form'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'

import type { QueryStatus } from 'react-query'
import type { FC } from 'react'
import type {
  TPraktikKinerjaDiskusiPersiapanFormValue,
  TPraktikKinerjaPerencanaanSummary,
  TSubmissionResponse,
} from 'types/kinerja-ps-pegawai'
import type { FieldPath, SubmitHandler, UseFormReturn } from 'react-hook-form'

const DialogConfirmation = ({
  onBack,
  onConfirm,
  isSubmitting = false,
  ...dialogProps
}: Omit<DialogProps, 'children'> & {
  isSubmitting?: boolean
  onBack?: () => void
  onConfirm: () => void
}) => {
  // contain state of "Mengisi sembari diskusi bersama Tim Kinerja"
  const [isCheckbox1Checked, setIsCheckbox1Checked] = useState(false)
  // contain state of "Mengetahui bahwa pendampingan akan dilakukan setelah ini"
  const [isCheckbox2Checked, setIsCheckbox2Checked] = useState(false)

  const isEnableConfirmButton = isCheckbox1Checked && isCheckbox2Checked

  return (
    <Dialog {...dialogProps}>
      <DialogOverlay />
      <DialogContent>
        <div className="p-6">
          <div className="mb-6">
            <Text variant="heading-md" className="mb-5">
              Saya menyatakan sudah:
            </Text>

            <Checkbox
              checked={isCheckbox1Checked}
              onChange={(e) => setIsCheckbox1Checked(e.target.checked)}
            >
              Mengisi sembari diskusi bersama Tim Kinerja
            </Checkbox>
            <Checkbox
              checked={isCheckbox2Checked}
              onChange={(e) => setIsCheckbox2Checked(e.target.checked)}
            >
              Mengetahui bahwa pendampingan akan dilakukan setelah ini
            </Checkbox>
          </div>

          <div className="flex gap-3">
            <Button size="lg" color="white" onClick={onBack}>
              Kembali
            </Button>
            <Button
              size="lg"
              color="blue"
              onClick={onConfirm}
              isLoading={isSubmitting}
              disabled={!isEnableConfirmButton || isSubmitting}
            >
              Konfirmasi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const MSG_ERR_REQUIRED = 'Wajib diisi'

const FField: FC<{
  form: UseFormReturn<TPraktikKinerjaDiskusiPersiapanFormValue, any>
  name: FieldPath<TPraktikKinerjaDiskusiPersiapanFormValue>
  question: string
}> = ({ form, name, question }) => {
  return (
    <FormField
      control={form.control}
      name={name}
      rules={{
        required: MSG_ERR_REQUIRED,
      }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            <Text variant="body-bold" className="mb-1">
              {question}
            </Text>
          </FormLabel>

          <FormControl>
            <Textarea
              placeholder="Ketik di sini"
              aria-label={question}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

type PSPelaksanaanDiskusiPersiapanFormProps = {
  formValues: TPraktikKinerjaDiskusiPersiapanFormValue
  perencanaanSummary: TPraktikKinerjaPerencanaanSummary
  isLoading: boolean
  isFetched: boolean

  submitStatus: QueryStatus
  onSubmitFormValues: ({
    values,
    saveAsDraft,
  }: {
    values: TPraktikKinerjaDiskusiPersiapanFormValue
    saveAsDraft: boolean
  }) => Promise<TSubmissionResponse>
}

export default function PSPelaksanaanDiskusiPersiapanForm({
  formValues,
  perencanaanSummary,
  isLoading,
  isFetched,
  submitStatus,
  onSubmitFormValues,
}: PSPelaksanaanDiskusiPersiapanFormProps) {
  const toast = useToast()
  const router = useRouter()
  const { trackEvent } = usePPSTrackEvent()

  const form = useForm<TPraktikKinerjaDiskusiPersiapanFormValue>({
    defaultValues: {
      tantangan: '',
      isu: '',
      npsn_satdik_pendamping: '',
      strategi: '',
    },
  })

  useEffect(() => {
    if (isFetched) {
      form.reset(formValues)
    }
  }, [form, formValues, isFetched])

  const formFieldWatcher = form.watch()

  const isFormEmpty = useMemo(() => {
    return Object.values(formFieldWatcher).every((value) => !value)
  }, [formFieldWatcher])

  const [isDialogConfirmationOpen, setIsDialogConfirmationOpen] =
    useState(false)
  const onSubmit: SubmitHandler<
    TPraktikKinerjaDiskusiPersiapanFormValue
  > = () => {
    setIsDialogConfirmationOpen(true)
  }

  const handleSubmitFormValues = async (saveAsDraft = true) => {
    try {
      await onSubmitFormValues({ values: form.getValues(), saveAsDraft })

      trackEvent('pelaksanaan pegawai', 'submit form A')

      toast({
        message: saveAsDraft ? 'Berhasil simpan draf!' : 'Berhasil kirim!',
        type: 'success',
      })

      if (window.history.state.idx <= 1) {
        router.push(
          `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
        )
      } else {
        router.back()
      }
    } catch (e) {
      console.error(e)
      toast({
        message: saveAsDraft
          ? 'Gagal simpan draf. Coba lagi.'
          : 'Gagal kirim. Coba lagi.',
      })
    } finally {
      setIsDialogConfirmationOpen(false)
    }
  }

  return (
    <>
      <div className="flex gap-6">
        <SimpleCard className="p-5 h-fit w-72">
          <div className="flex items-center gap-4 mb-6">
            <Image
              src="/illustrations/img-document-penci-fill.png"
              width={48}
              height={48}
              alt=""
            />

            <Text variant="heading-lg">Panduan</Text>
          </div>

          <Text>Isi sembari diskusi bersama Tim Kinerja</Text>
        </SimpleCard>

        <SimpleCard className="flex-1 !rounded mb-24">
          {isLoading && (
            <div
              data-testid="loading-spinner-diskusi-persiapan"
              className="flex justify-center w-full p-6"
            >
              <Spinner />
            </div>
          )}
          {isFetched && (
            <>
              <div className="flex flex-col gap-1 p-6 border-b rounded-t bg-blue-5 border-b-surface-subdued">
                <Text variant="heading-lg">
                  Isi sembari diskusi bersama Tim Kinerja
                </Text>
                <Text>
                  Sehingga Anda dapat mempersiapkan Pendampingan Satuan
                  Pendidikan dengan lebih matang.
                </Text>
              </div>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="flex flex-col gap-6 p-6"
                >
                  <SimpleCard className="flex-1 !rounded">
                    <div className="flex items-center gap-2 p-3 border-b rounded-t bg-gray-5 border-b-surface-subdued">
                      <Icon>check_circle_outline</Icon>
                      <Text variant="body-bold">
                        Indikator praktik kinerja pilihan anda
                      </Text>
                    </div>
                    <div className="flex flex-col gap-3 px-6 py-4">
                      <Text>{perencanaanSummary?.rhkPs?.nama ?? ''}</Text>

                      <Text
                        color="subdued"
                        dangerouslySetInnerHTML={{
                          __html: (
                            perencanaanSummary?.rhkPs?.keterangan ?? ''
                          ).replace(/\\n/g, '<br />'),
                        }}
                      />
                    </div>
                  </SimpleCard>

                  <FField
                    form={form}
                    name="tantangan"
                    question="1. Sebutkan minimal 3 tantangan yang pernah atau berpotensi Anda temukan saat pendampingan:"
                  />

                  <FField
                    form={form}
                    name="isu"
                    question="2. Sebutkan minimal 1 isu yang penting untuk menjadi fokus pendampingan:"
                  />

                  <FormField
                    control={form.control}
                    name="npsn_satdik_pendamping"
                    rules={{
                      required: MSG_ERR_REQUIRED,
                    }}
                    render={({ field: { name, onChange, value } }) => (
                      <FormItem>
                        <FormLabel>
                          <Text variant="body-bold" className="mb-1">
                            3. Pilih 1 satuan pendidikan untuk menjadi fokus
                            pendampingan:
                          </Text>
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            name={name}
                            onChange={onChange}
                            className="flex flex-col"
                            value={value}
                          >
                            {perencanaanSummary.satuanPendidikan.map(
                              (satdik) => (
                                <Radio
                                  key={satdik.npsn}
                                  value={satdik.npsn}
                                  className="mt-2"
                                  checked={value === satdik.npsn}
                                >
                                  {satdik.satuanPendidikan}
                                </Radio>
                              )
                            )}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />

                        <div className="flex items-start gap-2 p-4 !mt-2 border rounded-md bg-surface-informational-subdued border-interactive-default">
                          <Icon>info</Icon>
                          <Text variant="body">
                            <strong>
                              Jika data satuan pendidikan tidak sesuai:
                            </strong>{' '}
                            Mohon untuk tetap memilih salah satu satuan
                            pendidikan dan melanjutkan pengisian terlebih
                            dahulu. Setelah itu, silakan kabari Tim Kinerja di
                            luar sistem.
                          </Text>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FField
                    form={form}
                    name="strategi"
                    question="4. Jelaskan strategi Anda dalam melakukan pendampingan di satuan pendidikan tersebut:"
                  />

                  <div className="fixed bottom-0 left-0 right-0 z-10 flex justify-end w-full gap-4 px-24 py-4 bg-white border-t border-subdued">
                    <Button
                      color="white"
                      size="md"
                      className="!px-6"
                      onClick={() => handleSubmitFormValues(true)}
                      isLoading={submitStatus === 'loading'}
                      disabled={isFormEmpty || submitStatus === 'loading'}
                    >
                      Simpan draf
                    </Button>
                    <Button
                      color="blue"
                      size="md"
                      className={clsx(
                        '!px-6',
                        isFormEmpty &&
                          !form.formState.isDirty &&
                          '!bg-gray-30 !text-gray-50 !cursor-not-allowed'
                      )}
                      type="submit"
                      isLoading={submitStatus === 'loading'}
                      disabled={submitStatus === 'loading'}
                    >
                      Kirim
                    </Button>
                  </div>
                </form>
              </Form>
            </>
          )}
        </SimpleCard>
      </div>

      <DialogConfirmation
        isSubmitting={submitStatus === 'loading'}
        isOpen={isDialogConfirmationOpen}
        onConfirm={() => {
          handleSubmitFormValues(false).finally(() => {
            setIsDialogConfirmationOpen(false)
          })
        }}
        onBack={() => setIsDialogConfirmationOpen(false)}
      />
    </>
  )
}
